// Authentication Store
//
// This module provides the core state management for user authentication in the application.
// It manages the authentication state, user information, and provides getters/setters for
// various authentication states.
//
// The store uses SolidJS's createStore for reactive state management and integrates with
// the application's authentication service to maintain user session state.
import { createStore } from "solid-js/store";
import type { AuthStore, SetAuthStoreFunc } from "./auth.types";
import type { AuthenticatedUser } from "@storytell/cp-client";
import { UserStatus } from "@storytell/cp-client";
import {
  isLifecycleRegistered,
  isLifecycleDeleteRequested,
  isLifecycleError,
  isLifecycleGuest,
  isLifecycleLoading,
  isLifecycleMarkedForAbuse,
  isLifecyclePendingVerification,
} from "./auth.helpers";

// Creates the initial state for the authentication store.
// This represents the default state before any authentication attempts or user data is loaded.
export const initialAuthStore = (): AuthStore => ({
  state: "loading",
  token: undefined,
  authenticatedUser: undefined,
  error: undefined,
});

// newStore is a utility function that creates a new instance of the authentication store.
export const newStore = (store: AuthStore): AuthStore => store;

// Creates a new authentication store instance using SolidJS's createStore.
// This provides reactive state management for authentication-related data.
export const createAuthStore = () => createStore<AuthStore>(initialAuthStore());

// --- Getters ---

// Checks if the user is currently authenticated and registered.
// This state indicates the user has completed both registration and email verification.
export const getRegistered = (store: AuthStore) => isLifecycleRegistered(store.state);

// Checks if the user is currently in guest mode.
// Guest users have limited access and functionality compared to registered users.
export const getAuthIsGuest = (store: AuthStore) => isLifecycleGuest(store.state);

// Checks if the authentication state is currently loading.
// This state is typically shown during initial load or during authentication processes.
export const getAuthIsLoading = (store: AuthStore) => isLifecycleLoading(store.state);

// Checks if there is an authentication error.
// When true, the error message can be accessed from the store's error property.
export const getAuthIsError = (store: AuthStore) => isLifecycleError(store.state);

// Checks if the user has requested account deletion.
// This state is used to handle the account deletion workflow.
export const getAuthIsDeleteRequested = (store: AuthStore) => isLifecycleDeleteRequested(store.state);

// Checks if the user has been marked for abuse.
// This state is used to restrict access for users who have violated terms of service.
export const getAuthIsMarkedForAbuse = (store: AuthStore) => isLifecycleMarkedForAbuse(store.state);

// Checks if the user's email verification is pending.
// Users in this state may have limited access until verification is complete.
export const getAuthIsPendingVerification = (store: AuthStore) => isLifecyclePendingVerification(store.state);

// --- Setters ---

// Sets the authentication state to registered user with the provided user data.
// This is typically called after successful authentication and verification.
export const setAuthUser = (setStore: SetAuthStoreFunc, authenticatedUser: AuthenticatedUser) =>
  setStore({
    error: undefined,
    state: authenticatedUser.user.lifecycleStatus,
    authenticatedUser,
  });

// Sets the authentication state to error with the provided error message.
// This is used to handle authentication failures and display appropriate error messages.
export const setAuthError = (setStore: SetAuthStoreFunc, error: string) =>
  setStore({
    error,
    state: "error",
    authenticatedUser: undefined,
    token: undefined,
  });

// Resets the authentication store to its initial loading state.
// This is typically used when starting a new authentication process.
export const setAuthLoading = (setStore: SetAuthStoreFunc) => setStore(initialAuthStore());
